import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { LayoutDashboard, ClipboardCheck, MapPin, Users, LogOut, Map, Package, TrendingUp, Radio, Building2, Tag, Send, Copy, Check, Sliders, ShieldCheck, FileSearch, BarChart3, Target, Menu, X, BookOpen, Gauge, Settings, ChevronDown, Activity, BellRing, CalendarCheck, Hexagon, KeyRound, Clock3, Megaphone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { toast } from 'sonner';
import NotificationBell from '../../components/NotificationBell';
import { WindowsProvider, WindowsTaskbar } from '../../context/WindowsContext';
import { FULL_VERSION } from '../../version';

const SCREENS = [
  { match: /^\/admin\/?$/, title: 'Dashboard', code: 'AD-P01' },
  { match: /^\/admin\/prices$/, title: 'Capturas de competencia', code: 'AD-P02' },
  { match: /^\/admin\/price-changes$/, title: 'Diferencias de precios', code: 'AD-P03' },
  { match: /^\/admin\/tracking-requests$/, title: 'Tracking de precios', code: 'AD-P04' },
  { match: /^\/admin\/push-prices$/, title: 'Push de precios', code: 'AD-P05' },
  { match: /^\/admin\/stations$/, title: 'Estaciones', code: 'AD-P06' },
  { match: /^\/admin\/station-pricing$/, title: 'Precios por estación', code: 'AD-P07' },
  { match: /^\/admin\/departments$/, title: 'Departamentos', code: 'AD-P08' },
  { match: /^\/admin\/zones$/, title: 'Zonas', code: 'AD-P09' },
  { match: /^\/admin\/products$/, title: 'Productos', code: 'AD-P10' },
  { match: /^\/admin\/users$/, title: 'Usuarios', code: 'AD-P11' },
  { match: /^\/admin\/parameters$/, title: 'Parámetros', code: 'AD-P12' },
  { match: /^\/admin\/workload$/, title: 'Carga del equipo · Agentes y TMs', code: 'AD-P13' },
  { match: /^\/admin\/audit-log$/, title: 'Auditoría', code: 'AD-P14' },
  { match: /^\/admin\/metrics$/, title: 'Métricas de operación', code: 'AD-P15' },
  { match: /^\/admin\/markers$/, title: 'Recomendación de markers', code: 'AD-P16' },
  { match: /^\/admin\/price-monitor$/, title: 'Monitor de precios', code: 'AD-P17' },
  { match: /^\/admin\/notification-center$/, title: 'Centro de notificaciones', code: 'AD-P18' },
  { match: /^\/admin\/tracking-compliance$/, title: 'Cumplimiento tracking', code: 'AD-P19' },
];

const getScreen = (pathname) => {
  for (const s of SCREENS) if (s.match.test(pathname)) return s;
  return { title: 'PriceCapture · UNO', code: '' };
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screen = getScreen(location.pathname);
  const [pendingTracking, setPendingTracking] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [pendingPushes, setPendingPushes] = useState(0);
  const [pendingHH, setPendingHH] = useState(0);
  const [copiedCode, setCopiedCode] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const GROUP_ROUTES = {
    config: ['/admin/stations', '/admin/station-pricing', '/admin/zones', '/admin/products', '/admin/departments', '/admin/parameters', '/admin/markers'],
    security: ['/admin/users', '/admin/audit-log'],
    reports: ['/admin/workload', '/admin/metrics', '/admin/tracking-compliance'],
  };
  useEffect(() => {
    for (const [k, routes] of Object.entries(GROUP_ROUTES)) {
      if (routes.some(r => location.pathname.startsWith(r))) {
        setOpenGroups(o => (o[k] ? o : { ...o, [k]: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    let stopped = false;
    const loadCount = async () => {
      try {
        const r = await api.get('/price-change-requests', { params: { status_f: 'open' } });
        if (!stopped) setPendingTracking((r.data || []).length);
      } catch { /* noop */ }
      try {
        const c = await api.get('/prices/pending-count');
        if (!stopped) setPendingApprovals((c.data.pending || 0) + (c.data.flagged || 0));
      } catch { /* noop */ }
      try {
        const sp = await api.get('/station-pushes-pending-count');
        if (!stopped) setPendingPushes(sp.data.pending || 0);
      } catch { /* noop */ }
      try {
        const hh = await api.get('/happy-hours', { params: { status_f: 'pending' } });
        if (!stopped) setPendingHH((hh.data || []).length);
      } catch { /* noop */ }
    };
    loadCount();
    const t = setInterval(loadCount, 30 * 1000);
    return () => { stopped = true; clearInterval(t); };
  }, [location.pathname]);

  const copyCode = async () => {
    if (!screen.code) return;
    const ok = await copyToClipboard(screen.code);
    if (ok) {
      setCopiedCode(true);
      toast.success(`${screen.code} copiado`);
      setTimeout(() => setCopiedCode(false), 1400);
    } else {
      toast.error('No se pudo copiar');
    }
  };

  const items = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/admin/prices', icon: ClipboardCheck, label: 'Aprobaciones', badge: pendingApprovals },
    { to: '/admin/tracking-requests', icon: Send, label: 'Tracking precios', badge: pendingTracking },
    { to: '/admin/happy-hour', icon: Clock3, label: 'Happy Hour', badge: pendingHH },
    { to: '/admin/price-changes', icon: TrendingUp, label: 'Diferencias' },
    { to: '/admin/push-prices', icon: Radio, label: 'Push precios', badge: pendingPushes },
    { to: '/admin/comms', icon: Megaphone, label: 'Comunicados' },
  ];

  const groups = [
    { key: 'precios', label: 'Precios', icon: Tag, items: [
      { to: '/admin/current-prices', icon: Tag, label: 'Precios vigentes' },
      { to: '/admin/price-monitor', icon: Gauge, label: 'Monitor precios' },
    ]},
    { key: 'config', label: 'Configuración', icon: Settings, items: [
      { to: '/admin/stations', icon: MapPin, label: 'Estaciones' },
      { to: '/admin/station-pricing', icon: Tag, label: 'Precios / estación' },
      { to: '/admin/zones', icon: Map, label: 'Zonas' },
      { to: '/admin/products', icon: Package, label: 'Productos' },
      { to: '/admin/departments', icon: Building2, label: 'Departamentos' },
      { to: '/admin/trade-areas', icon: Hexagon, label: 'Trade Áreas' },
      { to: '/admin/parameters', icon: Sliders, label: 'Parámetros' },
      { to: '/admin/notification-center', icon: BellRing, label: 'Notificaciones' },
      { to: '/admin/markers', icon: Target, label: 'Markers' },
    ]},
    { key: 'security', label: 'Seguridad', icon: ShieldCheck, items: [
      { to: '/admin/users', icon: Users, label: 'Usuarios' },
      { to: '/admin/api-clients', icon: KeyRound, label: 'API Externa' },
      { to: '/admin/audit-log', icon: FileSearch, label: 'Auditoría' },
    ]},
    { key: 'reports', label: 'Reportes', icon: BarChart3, items: [
      { to: '/admin/workload', icon: Activity, label: 'Carga del equipo' },
      { to: '/admin/metrics', icon: BarChart3, label: 'Métricas' },
      { to: '/admin/tracking-compliance', icon: CalendarCheck, label: 'Cumplimiento tracking' },
      { to: '/admin/daily-report', icon: BarChart3, label: 'Reporte diario' },
    ]},
  ];

  const sidebarContent = (
    <>
      <Link to="/" className="px-6 py-6 flex items-center gap-2.5 border-b border-[#e8e5de]">
        <div className="w-8 h-8 rounded-md pc-logo-tile flex items-center justify-center">
          <span className="text-white font-semibold text-[15px] leading-none tracking-tight">P</span>
        </div>
        <div>
          <p className="text-[13px] font-medium leading-tight">PriceCapture</p>
          <p className="text-[10px] text-[#8a8579] tracking-wider uppercase">Admin</p>
        </div>
      </Link>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map(it => (
          <NavLink key={it.to} to={it.to} end={it.end} className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition ${isActive ? 'bg-[#0f1d6f] text-white' : 'text-[#3a3a3a] hover:bg-[#e7eeff]'}`}>
            <it.icon size={15} strokeWidth={1.6} />
            <span className="flex-1">{it.label}</span>
            {it.badge > 0 && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#b91c1c] text-white min-w-[18px] text-center" data-testid={`sidebar-badge-${it.to.split('/').pop()}`}>
                {it.badge}
              </span>
            )}
          </NavLink>
        ))}

        {groups.map(g => {
          const isOpen = openGroups[g.key];
          const hasActive = g.items.some(it => location.pathname.startsWith(it.to));
          return (
            <div key={g.key} className="pt-1">
              <button
                onClick={() => setOpenGroups(o => ({ ...o, [g.key]: !o[g.key] }))}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition ${hasActive && !isOpen ? 'bg-[#e7eeff] text-[#0f1d6f]' : 'text-[#3a3a3a] hover:bg-[#e7eeff]'}`}
                data-testid={`sidebar-group-${g.key}`}
              >
                <g.icon size={15} strokeWidth={1.6}/>
                <span className="flex-1 text-left">{g.label}</span>
                <ChevronDown size={13} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}/>
              </button>
              {isOpen && (
                <div className="mt-0.5 ml-4 pl-2 border-l border-[#e8e5de] space-y-0.5">
                  {g.items.map(it => (
                    <NavLink key={it.to} to={it.to} className={({isActive}) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] transition ${isActive ? 'bg-[#0f1d6f] text-white' : 'text-[#3a3a3a] hover:bg-[#e7eeff]'}`}>
                      <it.icon size={13.5} strokeWidth={1.6}/>
                      <span className="flex-1">{it.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="p-3 border-t border-[#e8e5de]">
        <a href="/manual-usuario.html" target="_blank" rel="noreferrer" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] text-[#3a3a3a] hover:bg-[#e7eeff]" data-testid="sidebar-manual-link">
          <BookOpen size={14}/> Manual de uso
        </a>
        <div className="px-3 py-2.5">
          <p className="text-[12px] font-medium truncate">{user?.name}</p>
          <p className="text-[10.5px] text-[#8a8579] truncate">{user?.email}</p>
        </div>
        <button onClick={() => { logout(); navigate('/'); }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] text-[#3a3a3a] hover:bg-[#f3f0e9]">
          <LogOut size={14}/> Cerrar sesión
        </button>
        <p className="px-3 pt-2 text-[10px] text-[#b5b0a4] tracking-wide" data-testid="admin-app-version">{FULL_VERSION}</p>
      </div>
    </>
  );

  return (
    <WindowsProvider>
    <div className="min-h-screen bg-[#f8f6f1] flex">
      {/* Sidebar (desktop) */}
      <aside className="w-60 bg-white border-r border-[#e8e5de] hidden lg:flex flex-col sticky top-0 h-screen">
        {sidebarContent}
      </aside>

      {/* Sidebar (mobile drawer) */}
      {menuOpen && (
        <div className="fixed inset-0 z-[1100] lg:hidden" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/45" onClick={() => setMenuOpen(false)} data-testid="admin-drawer-overlay"/>
          <aside className="absolute left-0 top-0 h-full w-64 max-w-[85vw] bg-white flex flex-col shadow-xl" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <button onClick={() => setMenuOpen(false)} className="absolute right-3 w-8 h-8 rounded-full hover:bg-[#f3f0e9] flex items-center justify-center" style={{ top: 'max(1rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))' }} data-testid="admin-drawer-close" aria-label="Cerrar menú">
              <X size={16}/>
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-[#f8f6f1]/85 backdrop-blur-md border-b border-[#e8e5de] px-4 sm:px-8 pb-4 flex items-center justify-between gap-3" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))' }}>
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setMenuOpen(true)} className="lg:hidden w-9 h-9 shrink-0 rounded-lg border border-[#e8e5de] bg-white flex items-center justify-center" data-testid="admin-menu-btn" aria-label="Abrir menú">
              <Menu size={17}/>
            </button>
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#8a8579] flex items-center gap-1.5">
                <span className="hidden sm:inline">Panel administrador</span>
                {screen.code && <span className="text-[#cfcabe] hidden sm:inline">·</span>}
                {screen.code && (
                  <button
                    type="button"
                    onDoubleClick={copyCode}
                    title={`Doble click para copiar ${screen.code}`}
                    className="font-mono normal-case tracking-normal inline-flex items-center gap-1 px-1.5 py-0.5 -my-0.5 rounded hover:bg-[#1c1c1c]/5 transition group select-none"
                    data-testid="admin-screen-code"
                  >
                    <span>{screen.code}</span>
                    {copiedCode ? <Check size={11} className="text-[#15803d]"/> : <Copy size={10.5} className="text-[#cfcabe] group-hover:text-[#8a8579] transition"/>}
                  </button>
                )}
              </p>
              <h1 className="text-[15px] font-medium tracking-tight truncate" data-testid="admin-screen-title">{screen.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <NotificationBell variant="light"/>
          </div>
        </header>
        <div className="p-4 sm:p-8 admin-scroll">
          <Outlet />
        </div>
      </div>
      <WindowsTaskbar />
    </div>
    </WindowsProvider>
  );
};

export default AdminLayout;
